import { CalculateServiceCostResult, Service, ServiceCostSummaryHomeCare } from '@anispi-reporting-system/types';
import { Card } from '@mui/material';
import Box from '@mui/material/Box';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { Button } from './Button';
import { ResultHomeCare } from './ResultHomeCare';
import { SendResultDialog } from './SendResultDialog';
import { SnackbarMessageProps } from './SnackbarMessage';
import type { UploadFile } from '../hooks/useForm';
import { calculateServiceCost } from '../lib/Api';

interface Props {
  companyName: string;
  companyNo: string;
  officeNo: string;
  service: Service;
  serviceCost: CalculateServiceCostResult['serviceCost'] | undefined;
  setService: React.Dispatch<React.SetStateAction<Service>>;
  setServiceCost: React.Dispatch<React.SetStateAction<CalculateServiceCostResult['serviceCost'] | undefined>>;
  setSnackbarMessage: React.Dispatch<React.SetStateAction<Omit<SnackbarMessageProps, 'onClose'>>>;
  setUploadFileSe: React.Dispatch<React.SetStateAction<UploadFile>>;
  setUploadFileSv: React.Dispatch<React.SetStateAction<UploadFile>>;
  uploadFileSe: UploadFile;
  uploadFileSv: UploadFile;
}

const Result: FunctionComponent<Props> = ({
  companyName,
  companyNo,
  officeNo,
  service,
  serviceCost,
  setService,
  setServiceCost,
  setSnackbarMessage,
  setUploadFileSe,
  setUploadFileSv,
  uploadFileSe,
  uploadFileSv,
}) => {
  const [openSendResult, setOpenSendResult] = useState(false);
  const [loading, setLoading] = useState(false);

  const isInvalid = () => {
    if (!companyNo || !officeNo || !service) {
      return true;
    }

    if (!uploadFileSe.filename || !uploadFileSv.filename) {
      return true;
    }

    return false;
  };

  const handleCalculated = async () => {
    try {
      setLoading(true);
      const calculatedServiceCost = await calculateServiceCost({
        companyNo,
        fileNameSe: uploadFileSe.uploadKey,
        fileNameSv: uploadFileSv.uploadKey,
        officeNo,
        service,
      });

      if (calculatedServiceCost.success) {
        setServiceCost(calculatedServiceCost.serviceCost);
        setOpenSendResult(true);
      } else {
        setSnackbarMessage({
          message: '計算結果の取得に失敗しました。',
          open: true,
          severity: 'error',
        });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      setSnackbarMessage({
        message: '計算結果の取得に失敗しました。',
        open: true,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    setService('RS');
    setUploadFileSe({
      filename: '',
      uploadKey: '',
    });
    setUploadFileSv({
      filename: '',
      uploadKey: '',
    });
    setServiceCost(undefined);
  };

  useEffect(() => {
    setServiceCost(undefined);
  }, [companyNo, uploadFileSe, uploadFileSv, officeNo, service, setServiceCost]);

  return (
    <Box className="w-full">
      <Typography color="text.secondary" variant="h5" gutterBottom>
        計算結果
      </Typography>

      <Box className="space-y-3">
        <Card variant="outlined">
          <CardContent>
            <Box className="flex max-h-full w-full flex-row items-center justify-center">
              {serviceCost && (service === 'HOMECARE-RS' || service === 'HOMECARE-RY') && serviceCost && (
                <Box className="w-full overflow-y-scroll lg:w-1/2">
                  <ResultHomeCare service={service} serviceCostSummary={serviceCost as ServiceCostSummaryHomeCare} />
                </Box>
              )}

              <Button className="w-60" loading={loading} variant="outlined" disabled={isInvalid()} onClick={handleCalculated}>
                計算結果を表示する
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {serviceCost && (
        <SendResultDialog
          companyName={companyName}
          companyNo={companyNo}
          officeNo={officeNo}
          setSnackbarMessage={setSnackbarMessage}
          service={service}
          serviceCost={serviceCost}
          uploadFileSe={uploadFileSe}
          uploadFileSv={uploadFileSv}
          open={openSendResult}
          setOpen={setOpenSendResult}
          onComplete={() => handleComplete()}
        />
      )}
    </Box>
  );
};

export { Result };
